/**
 * Side-effectful glue between the pure save-load logic and the rest
 * of the app: reads BOTH dashboard modes' state for export, and
 * writes an `AppliedImport` back through Dexie + stores.
 *
 * Kept out of the pure modules so the export/import logic stays
 * trivially unit-testable.
 */
import {
  configDb,
  layoutKey,
  type DashboardMode,
} from '@/persistence/config-db';
import type { DashboardLayoutItem } from '@/types/dashboard-config';
import { buildConfig, serializeConfig, type ModeSliceInput } from './export-config';
import { applyImport, type AppliedImport } from './import-config';
import type { DashboardConfig } from '@/types/dashboard-config';
import type { ColumnResolution } from './import-config';
import type { ColumnMeta, ColumnOverrides } from '@/types/dataset';
import type { WidgetConfig } from '@/types/widget';
import type { ColumnTransform } from '@/types/query';

/**
 * Shape of the serialized RGL layouts entry as stored in Dexie
 * (see `use-persisted-layout.ts`). Only the `lg` breakpoint survives
 * the export round-trip; other breakpoints are regenerated by
 * `computeZoneLayout` on load.
 */
type StoredLayouts = { lg?: DashboardLayoutItem[] };

async function readZoneLayout(
  zone: 'kpi' | 'chart',
  datasetId: string,
  mode: DashboardMode,
): Promise<DashboardLayoutItem[]> {
  const tbl = zone === 'kpi' ? configDb.kpiLayouts : configDb.chartLayouts;
  try {
    const entry = await tbl.get(layoutKey(datasetId, mode));
    if (!entry) return [];
    const parsed = JSON.parse(entry.layouts) as StoredLayouts;
    return parsed.lg ?? [];
  } catch {
    return [];
  }
}

async function readModeSlice(
  datasetId: string,
  mode: DashboardMode,
): Promise<ModeSliceInput> {
  const [widgets, pinnedEntry, kpiLayout, chartLayout] = await Promise.all([
    configDb.widgets
      .where('[datasetId+mode]')
      .equals([datasetId, mode])
      .toArray()
      .catch(() => [] as WidgetConfig[]),
    configDb.pinnedFilters.get(layoutKey(datasetId, mode)).catch(() => undefined),
    readZoneLayout('kpi', datasetId, mode),
    readZoneLayout('chart', datasetId, mode),
  ]);
  return {
    widgets,
    pinnedFilters: pinnedEntry?.columns ?? [],
    layouts: { kpi: kpiLayout, chart: chartLayout },
  };
}

/**
 * Build + serialize a dashboard config for the given dataset.
 * Bundles BOTH modes (normal + compare) so the on-disk JSON is
 * self-contained (D-020 follow-up).
 */
export async function exportDashboard(params: {
  datasetId: string;
  currentColumns: ColumnMeta[];
  columnOverrides: ColumnOverrides;
  valueGroupings: Record<string, ColumnTransform>;
}): Promise<{ json: string; config: DashboardConfig }> {
  const [normal, compare, compareConfig] = await Promise.all([
    readModeSlice(params.datasetId, 'normal'),
    readModeSlice(params.datasetId, 'compare'),
    configDb.compareConfigs.get(params.datasetId).catch(() => undefined),
  ]);
  const config = buildConfig({
    currentColumns: params.currentColumns,
    modes: { normal, compare },
    columnOverrides: params.columnOverrides,
    valueGroupings: params.valueGroupings,
    compareConfig: compareConfig ?? null,
  });
  return { json: serializeConfig(config), config };
}

/**
 * Trigger a browser download for the given JSON payload.
 * Must be called synchronously from a user-gesture handler so Firefox
 * doesn't treat it as a popup.
 */
export function downloadJson(filename: string, json: string): void {
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke on the next tick so the navigation has a chance to start.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * Persist an `AppliedImport` result through Dexie. Replaces BOTH
 * modes' widgets / pinned filters / layouts and the compare config.
 * Column overrides + value groupings are dataset-scope (apply across
 * modes equally). The UI is expected to reload its stores after this
 * resolves.
 */
export async function persistImport(
  targetDatasetId: string,
  applied: AppliedImport,
): Promise<void> {
  await configDb.transaction(
    'rw',
    [
      configDb.widgets,
      configDb.pinnedFilters,
      configDb.columnOverrides,
      configDb.valueGroupings,
      configDb.kpiLayouts,
      configDb.chartLayouts,
      configDb.compareConfigs,
    ],
    async () => {
      for (const mode of ['normal', 'compare'] as const) {
        const slice = applied.modes[mode];
        // Drop any existing widgets for this (dataset, mode) pair so we
        // don't merge stale ones with the imported set.
        const existing = await configDb.widgets
          .where('[datasetId+mode]')
          .equals([targetDatasetId, mode])
          .primaryKeys();
        if (existing.length > 0) {
          await configDb.widgets.bulkDelete(existing);
        }
        if (slice.widgets.length > 0) {
          await configDb.widgets.bulkPut(
            slice.widgets.map((w) => ({ ...w, mode })),
          );
        }
        await configDb.pinnedFilters.put({
          key: layoutKey(targetDatasetId, mode),
          datasetId: targetDatasetId,
          mode,
          columns: slice.pinnedFilters,
          updatedAt: Date.now(),
        });
        const now = Date.now();
        await configDb.kpiLayouts.put({
          key: layoutKey(targetDatasetId, mode),
          datasetId: targetDatasetId,
          mode,
          layouts: JSON.stringify({ lg: slice.layouts.kpi }),
          updatedAt: now,
        });
        await configDb.chartLayouts.put({
          key: layoutKey(targetDatasetId, mode),
          datasetId: targetDatasetId,
          mode,
          layouts: JSON.stringify({ lg: slice.layouts.chart }),
          updatedAt: now,
        });
      }

      // Column overrides and value groupings live at dataset scope
      // (not mode scope) because they describe the data's shape, not
      // the dashboard's layout. They affect both modes equally.
      await configDb.columnOverrides.put({
        datasetId: targetDatasetId,
        overrides: applied.columnOverrides,
        updatedAt: Date.now(),
      });

      await configDb.valueGroupings.put({
        datasetId: targetDatasetId,
        groupings: applied.valueGroupings,
        updatedAt: Date.now(),
      });

      if (applied.compareConfig) {
        await configDb.compareConfigs.put({
          datasetId: targetDatasetId,
          dateColumn: applied.compareConfig.dateColumn,
          granularity: applied.compareConfig.granularity,
          periodA: { ...applied.compareConfig.periodA },
          periodB: { ...applied.compareConfig.periodB },
          updatedAt: Date.now(),
        });
      } else {
        // Keep compare-config consistent with the imported bundle —
        // an export with no compareConfig should restore an empty one.
        await configDb.compareConfigs.delete(targetDatasetId);
      }
    },
  );
}

/**
 * End-to-end helper used by the review modal: apply + persist.
 */
export async function performImport(
  targetDatasetId: string,
  config: DashboardConfig,
  resolutions: ColumnResolution[],
): Promise<AppliedImport> {
  const applied = applyImport(config, resolutions, targetDatasetId);
  await persistImport(targetDatasetId, applied);
  return applied;
}
